import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { equipmentService, type EquipmentSearchResult } from '@/services/equipment.service';
import { useDebounce } from '@/hooks/use-debounce';

const INPUT_CLASS =
  'flex h-10 w-full rounded-lg border border-input bg-card pl-9 pr-9 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 disabled:opacity-50';

/** ModelNumber | SerialNumber | AssetNumber */
function formatLabel(r: EquipmentSearchResult): string {
  return `${r.modelNumber ?? '—'} | ${r.serialNumber} | ${r.assetNumber}`;
}

/**
 * Enterprise equipment autocomplete.
 * Server-side, debounced (300ms) typeahead — never loads the full equipment
 * list into the browser. Keyboard navigable (↑/↓/Enter/Esc). Scales to
 * 10,000+ records because only the top matches for the current term are fetched.
 */
export function EquipmentSearchCombobox({
  value,
  initialLabel,
  onSelect,
  disabled,
}: {
  value: string;
  initialLabel?: string;
  onSelect: (result: EquipmentSearchResult | null) => void;
  disabled?: boolean;
}) {
  const [inputValue, setInputValue] = useState(initialLabel ?? '');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounced = useDebounce(inputValue.trim(), 300);

  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Only query when the user is actively searching (dropdown open) and has a term.
  // When a selection is committed we close the dropdown, which disables the query.
  const enabled = open && debounced.length >= 1;

  const { data, isFetching } = useQuery({
    queryKey: ['equipment-search', debounced],
    queryFn: () => equipmentService.search(debounced, 20),
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const results = enabled ? (data?.items ?? []) : [];

  // Keep the displayed text in sync if the parent changes the preselected label
  // (e.g. opening the Edit modal for a different intake).
  useEffect(() => {
    setInputValue(initialLabel ?? '');
  }, [initialLabel]);

  // Close on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const commit = (r: EquipmentSearchResult) => {
    onSelect(r);
    setInputValue(formatLabel(r));
    setOpen(false);
    setActiveIndex(-1);
  };

  const clear = () => {
    onSelect(null);
    setInputValue('');
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleChange = (text: string) => {
    setInputValue(text);
    setOpen(true);
    setActiveIndex(-1);
    // Typing invalidates any committed selection until a new one is picked.
    if (value) onSelect(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && results[activeIndex]) {
        e.preventDefault();
        commit(results[activeIndex]!);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const showDropdown = open && debounced.length >= 1;

  return (
    <div ref={containerRef} className="relative">
      <FontAwesomeIcon
        icon={faSearch}
        className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
      />
      <input
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined}
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder="Search by model, serial, asset, name, or customer…"
        className={INPUT_CLASS}
        autoComplete="off"
      />
      {isFetching ? (
        <FontAwesomeIcon
          icon={faSpinner}
          className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground"
        />
      ) : inputValue && !disabled ? (
        <button
          type="button"
          onClick={clear}
          className="absolute right-2.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-destructive"
          aria-label="Clear equipment selection"
        >
          <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
        </button>
      ) : null}

      {showDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-border bg-card py-1 shadow-lg"
        >
          {results.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              {isFetching ? 'Searching…' : 'No equipment found.'}
            </li>
          ) : (
            results.map((r, i) => (
              <li
                key={r.id}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(r);
                }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`cursor-pointer px-3 py-2 ${i === activeIndex ? 'bg-muted' : ''}`}
              >
                <div className="font-mono text-sm text-foreground">
                  <span className="font-medium">{r.modelNumber ?? '—'}</span>
                  <span className="text-muted-foreground">
                    {' '}
                    | {r.serialNumber} | {r.assetNumber}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.equipmentName}
                  {r.customerName ? ` · ${r.customerName}` : ''}
                </div>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
